import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Flatten a nested object into a flat object with dot notation keys.
 * @param {Object} obj - The object to flatten.
 * @param {string} [prefix=''] - The prefix for nested keys.
 * @returns {Object} - The flattened object.
 */
const flattenObject = (obj, prefix = '') => {
  let result = {};
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(value)) {
      // Handle arrays (e.g., product_items) by converting each element into individual fields
      value.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          Object.assign(result, flattenObject(item, `${newKey}[${index}]`));
        } else {
          result[`${newKey}[${index}]`] = item;
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      // Recursively flatten nested objects
      Object.assign(result, flattenObject(value, newKey));
    } else {
      // Check if the value contains a decimal point and digits (i.e., it's a float)
      if (typeof value === 'string' && value.match(/^\d*\.\d+$/)) {
        result[newKey] = parseFloat(value).toFixed(2); // Convert to float and round to two decimal places
      } else if (typeof value === 'number') {
        result[newKey] = value.toFixed(2);
      } else {
        // Handle null and other values
        result[newKey] = value === null ? '' : value; // Replace null with an empty string
      }
    }
  }
  return result;
};

/**
 * Export data as CSV
 * @param {Array<Object>} data - Array of objects to be converted to CSV
 * @param {string} filename - Name of the CSV file
 */
// export const exportToCSV = (data, filename) => {
//   const flattenedData = data.map(item => flattenObject(item));
//   const csv = Papa.unparse(flattenedData);
//   const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
//   saveAs(blob, `${filename}.csv`);
// };
export const exportToCSV = (data, filename) => {
  const flattenedData = data.map(item => flattenObject(item));

  // Get a complete list of all keys used
  const allKeys = Array.from(
    new Set(flattenedData.flatMap(obj => Object.keys(obj)))
  );

  // Ensure each row has all keys (fill missing ones with empty strings)
  const normalizedData = flattenedData.map(obj => {
    const newObj = {};
    allKeys.forEach(key => {
      newObj[key] = key in obj ? obj[key] : '';
    });
    return newObj;
  });

  const csv = Papa.unparse(normalizedData);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${filename}.csv`);
};

/**
 * Export data as Excel
 * @param {Array<Object>} data - Array of objects to be converted to Excel
 * @param {string} filename - Name of the Excel file
 */
export const exportToExcel = (data, filename) => {
  const flattenedData = data.map(item => flattenObject(item));
  const ws = XLSX.utils.json_to_sheet(flattenedData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export data as PDF with custom page size
 * @param {Array<Object>} data - Array of objects to be converted to PDF
 * @param {string} filename - Name of the PDF file
 */
export const exportToPDF = (data, filename) => {
  const flattenedData = data.map(item => flattenObject(item));
  const doc = new jsPDF('l', 'mm', 'a0'); // Use A0 size for more space

  if (flattenedData.length === 0) {
    console.error('No data to export');
    return;
  }

  const columns = Object.keys(flattenedData[0]);
  const rows = flattenedData.map(item => columns.map(col => item[col] ? String(item[col]) : ''));

  // Calculate dynamic column widths based on header and content lengths
  const columnWidths = columns.map((col, index) => {
    const headerWidth = doc.getTextWidth(col) + 10; // Add padding for header
    const maxContentWidth = Math.max(...rows.map(row => doc.getTextWidth(row[index]))) + 10; // Add padding for content
    return Math.min(Math.max(headerWidth, maxContentWidth), 15); // Reduce max width to fit more columns
  });

  const tableOptions = {
    head: [columns],
    body: rows,
    theme: 'striped',
    styles: {
      fontSize: 4, // Smaller font size for better fit
      cellPadding: 1, // Smaller cell padding
      overflow: 'linebreak', // Allow text to wrap
      halign: 'left',
      valign: 'top',
    },
    columnStyles: columnWidths.reduce((acc, width, index) => {
      acc[index] = { cellWidth: width }; // Set dynamic column width
      return acc;
    }, {}),
    margin: { top: 10, bottom: 10, left: 5, right: 5 }, // Minimize margins
    pageBreak: 'auto',
    tableWidth: 'auto',
  };

  // Generate the table
  doc.autoTable(tableOptions);

  // Save the PDF directly
  doc.save(`${filename}.pdf`);
};